package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.StoreConnectionDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LConfirmTone
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr

/** Existing connections can be resumed; new credentials stay inside the wizard's lifetime. */
@Composable
internal fun StoreConnectAndBind(
    platform: StorePlatform,
    usable: List<StoreConnectionDto>,
    data: StoresPageData,
    runner: StoreActionRunner,
    onBindingChanged: () -> Unit,
) {
    var adding by remember(platform) { mutableStateOf(false) }
    var chosenId by remember(platform, usable) { mutableStateOf(usable.firstOrNull()?.id.orEmpty()) }
    var busy by remember(platform) { mutableStateOf(false) }
    val chosen = usable.firstOrNull { it.id == chosenId } ?: usable.firstOrNull()
    if (!adding && usable.isNotEmpty()) {
        usable.forEach { connection -> StoreConnectionRow(connection, data.orgSlug, runner, onBindingChanged) }
        if (usable.size > 1) {
            LSelect(
                value = chosen?.id.orEmpty(), options = usable.map { LSelectOption(it.id, it.label) },
                onValueChange = { chosenId = it }, modifier = Modifier.fillMaxWidth().styleModifier { property("min-width", "0"); property("max-width", "100%") }, label = t("app.projects.slug.stores_connect_forms.connection", "Connection"), enabled = !busy,
            )
        }
    }
    key(if (adding) "new" else chosen?.id ?: "new") {
        StoreConnectionWizard(
            platform, if (adding) null else chosen, data, runner,
            onBusy = { busy = it }, onBound = onBindingChanged,
        )
    }
    if (usable.isNotEmpty()) {
        LButton(
            if (adding) t("app.projects.slug.stores_connect_forms.use_existing", "Use an existing connection")
            else t("app.projects.slug.stores_connect_forms.add_another", "Add another connection"),
            onClick = { adding = !adding }, variant = LButtonVariant.GHOST, enabled = !busy,
        )
    }
}

/** One live connection, with the way to remove it. */
@Composable
private fun StoreConnectionRow(
    connection: StoreConnectionDto,
    orgSlug: String,
    runner: StoreActionRunner,
    onChanged: () -> Unit,
) {
    var confirming by remember(connection.id) { mutableStateOf(false) }
    var busy by remember(connection.id) { mutableStateOf(false) }
    Column(Modifier.fillMaxWidth().gap(Space.XS)) {
        StoreConnectionSummary(connection)
        Row(Modifier.gap(Space.SM)) {
            LButton(
                t("app.projects.slug.stores_connect_forms.disconnect", "Disconnect"),
                onClick = { confirming = true },
                variant = LButtonVariant.GHOST,
                size = LButtonSize.SM,
                enabled = runner.canManage && !busy,
            )
        }
    }
    LConfirmDialog(
        open = confirming,
        title = tf(
            "app.projects.slug.stores_connect_forms.disconnect_title",
            "Disconnect “{label}”?",
            "label" to connection.label,
        ),
        confirmLabel = t("app.projects.slug.stores_connect_forms.disconnect", "Disconnect"),
        onConfirm = {
            runner.run(
                setBusy = { busy = it },
                failure = tr("app.projects.slug.stores_connect_forms.disconnect_failed", "Couldn't remove this connection. Retry?"),
                transport = { ApiClient.deleteStoreConnection(orgSlug, connection.id) },
            ) {
                confirming = false
                runner.notify(
                    tr("app.projects.slug.stores_connect_forms.disconnected_toast", "Connection removed"),
                    LToastVariant.SUCCESS,
                    4_000,
                )
                onChanged()
            }
        },
        onCancel = { confirming = false },
        tone = LConfirmTone.DANGER,
        consequenceTitle = t("app.projects.slug.stores_connect_forms.disconnect_consequence_title", "Lingvo forgets this key"),
        consequence = t(
            "app.projects.slug.stores_connect_forms.disconnect_consequence",
            "Lingvo deletes its copy of the key. The key itself keeps working in the store until you revoke it there.",
        ),
        busy = busy,
    )
}
